import { File } from '@phosphor-icons/react'
import { useQuery } from '@tanstack/react-query'
import React, { useContext, useMemo, useRef } from 'react'

import CollapsibleCard from '@/components/shared/common/CollapsibleCard/CollapsibleCard'
import EditorAddExternalAttchments from '@/components/shared/components/attachments/EditorAddExternalAttchments'
import type { IEditorExternalAttchmentsListProps } from '@/components/shared/components/attachments/EditorExternalAttchmentsList'
import EditorExternalAttchmentsList from '@/components/shared/components/attachments/EditorExternalAttchmentsList'
import Alert from '@/components/shared/ui/Alert'
import { SpinnerIcon } from '@/components/shared/ui/Icons'
import { queryClient } from '@/config/queryClient'
import HNContext from '@/context/HNContext'
import {
  canAddAttachments,
  canViewAttachments,
} from '@/lib/helpers/rules/postACLRule'
import { getAttachments } from '@/models/Common'
import type { IChangelog } from '@/types/changelog'
import type { IComment } from '@/types/comment'
import type { IResourceTypes } from '@/types/common'
import type { IAttachment } from '@/types/organization'
import type { IPost } from '@/types/post'

interface IPropTypes {
  resourceId: string
  resourceType: IResourceTypes
  resource?: IPost | IComment | IChangelog
  showLoading?: boolean
  hideEmptyState?: boolean
}
export default function SinglePostAttachments({
  resourceId,
  resourceType,
  resource,
  showLoading,
  hideEmptyState,
}: IPropTypes) {
  const attachmentUploaderRef = useRef<IEditorExternalAttchmentsListProps>(null)
  const { userProfile, isAdmin } = useContext(HNContext)

  const canView: boolean = useMemo(() => {
    if (!resource) return false
    return canViewAttachments(resource, resourceType, userProfile)
  }, [resource, resourceType, userProfile])

  const canAdd: boolean = useMemo(() => {
    if (!resource) return false
    return canAddAttachments(resource, userProfile) && !!isAdmin
  }, [resource, userProfile, isAdmin])

  const {
    data: attachments = [],
    isLoading: loading,
    error,
  } = useQuery<IAttachment[], Error>({
    queryKey: ['attachments', resourceId, resourceType],
    queryFn: () => getAttachments(resourceId, resourceType),
    enabled: !!resourceId && !!resourceType && canView,
  })

  const handleNewAttachments = (newAttachments: IAttachment[]) => {
    queryClient.setQueryData(
      ['attachments', resourceId, resourceType],
      () => newAttachments
    )
  }

  // const getActions = () => {
  //   return (
  //     <EditorAddExternalAttchments
  //       buttonSize='xs'
  //       onAdd={(files) => {
  //         attachmentUploaderRef.current?.attachFiles(files)
  //       }}
  //       buttonProps={{
  //         children: `Add Attachment`,
  //         variant: 'outline',
  //         size: 'xxs',
  //         fab: false,
  //       }}
  //     />
  //   )
  // }
  if (loading && showLoading)
    return (
      <div className='min-h-44 flex max-h-60 justify-center overflow-y-auto rounded-lg p-2'>
        <SpinnerIcon className='my-5 h-8 w-8 animate-spin text-primary' />
      </div>
    )
  if (error) return <Alert type='error' message={error.message} />

  if (hideEmptyState && !attachments.length) return null
  if (!canView) return null

  return (
    <CollapsibleCard
      title={`Attachments (${attachments.length})`}
      collapseLocalKey={`${resourceType}-attachments`}
      icon={<File />}
      defaultCollapsed={false}
    >
      <div className='flex flex-col gap-2'>
        {canAdd && (
          <EditorAddExternalAttchments
            onAdd={(files) => attachmentUploaderRef.current?.attachFiles(files)}
            dropzone
          />
        )}
        <EditorExternalAttchmentsList
          defaultAttachments={attachments}
          onUpdate={(newAttachments) => {
            handleNewAttachments(newAttachments)
          }}
          ref={attachmentUploaderRef}
          resourceId={resourceId}
          resourceType={resourceType}
          resource={resource}
        />
      </div>
    </CollapsibleCard>
  )
}
